/**
 * @file path-edit-coordinator.ts
 * @description Wires up the SVG Path Node Editor.
 *
 * Responsibilities:
 *  - Registers the PathEdit pseudo-tool via window override (core is read-only)
 *  - Creates and exposes window.pathNodeEditor (PathNodeEditor instance)
 *  - Creates and exposes window.pathNodeOverlay (PathNodeOverlay instance)
 *  - Listens for EventBus events to activate/deactivate editing
 *  - Handles keyboard shortcuts (Delete, A, C, Escape, Ctrl+Z)
 *  - Listens for PATH_NODES_CHANGED to update the layer's shape data
 */

import { g, layers } from '@hcie/core';
import { EventBus } from '@hcie/shared';
import { PathNodeEditor, PathEditAction } from '@hcie/tools';
import { PathNodeOverlay } from '@hcie/canvas-ui';

// ─── Tool definition (window override — core is read-only) ───────────────────

const PATH_EDIT_TOOL_ID = 'btn-path-edit';

function registerPathEditTool(): void {
    const Tool = (window as any).Tool;
    if (Tool && !Tool.PathEdit) {
        Tool.PathEdit = {
            id: PATH_EDIT_TOOL_ID,
            name: 'Path Edit',
        };
    }
}

// ─── State ────────────────────────────────────────────────────────────────────

let _editor: PathNodeEditor | null = null;
let _overlay: PathNodeOverlay | null = null;

// Track the shape being edited so we can write changes back to layer data
let _editingShapeId: string | null = null;
let _editingLayerIndex: number = -1;
let _beforePathData: string = '';

// ─── Init ─────────────────────────────────────────────────────────────────────

export function initPathEditCoordinator(): void {
    registerPathEditTool();

    _editor  = new PathNodeEditor();
    _overlay = new PathNodeOverlay();

    // Expose globally so event-dispatcher.ts can reach them without imports
    (window as any).pathNodeEditor  = _editor;
    (window as any).pathNodeOverlay = _overlay;

    // ── EventBus subscriptions ────────────────────────────────────────────────

    // When the user double-clicks (or selects) a path shape, activate the editor
    EventBus.on('ACTIVATE_PATH_EDIT', (data: { shapeId: string; layerIndex: number; pathData: string }) => {
        _editingShapeId    = data.shapeId;
        _editingLayerIndex = data.layerIndex;
        _beforePathData    = data.pathData;

        if (_editor) {
            _editor.activate(data.shapeId, data.pathData);
            // Sync zoom so hit radii are correct
            _editor.setZoom(g.zoom || 1);
        }
    });

    // Write path changes back to the layer's shape data
    EventBus.on('PATH_NODES_CHANGED', (data: { shapeId: string; pathData: string; layerIndex?: number }) => {
        const li = data.layerIndex ?? _editingLayerIndex;
        if (li < 0 || !layers[li]) return;
        const layer = layers[li];
        const shape = (layer.shapes || []).find((s: any) => s.id === data.shapeId);
        if (shape) {
            const prev = shape.pathData;
            shape.pathData = data.pathData;

            // Push undo action
            const action = new PathEditAction(data.shapeId, li, prev, data.pathData);
            EventBus.emit('PUSH_HISTORY', action);
        }
        EventBus.emit('RENDER_LAYERS');
    });

    // Keep editor zoom in sync
    EventBus.on('ZOOM_CHANGED', (zoom: number) => {
        _editor?.setZoom(zoom);
    });

    // ── Keyboard shortcuts ────────────────────────────────────────────────────

    document.addEventListener('keydown', (e: KeyboardEvent) => {
        if (!_editor?.isActive) return;
        if (g.current_tool?.id !== PATH_EDIT_TOOL_ID) return;

        // Don't hijack when typing in an input/textarea
        const tag = (e.target as HTMLElement)?.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA') return;

        switch (e.key) {
            case 'Delete':
            case 'Backspace':
                e.preventDefault();
                _editor.deleteSelectedNode();
                break;

            case 'a':
            case 'A':
                e.preventDefault();
                if (_editor) _editor.addNodeAt(g.pX ?? 0, g.pY ?? 0);
                break;

            case 'c':
            case 'C':
                if (!e.ctrlKey && !e.metaKey) {
                    e.preventDefault();
                    _editor.cycleSelectedNodeType();
                }
                break;

            case 'Escape':
                e.preventDefault();
                _editor.deactivate();
                // Switch back to VectorSelect tool
                if (typeof (window as any).selectTool === 'function') {
                    (window as any).selectTool((window as any).Tool?.VectorSelect);
                }
                break;

            case 'z':
            case 'Z':
                if (e.ctrlKey || e.metaKey) {
                    // Let the global history coordinator handle it
                }
                break;
        }
    });
}

/** Programmatically activates path editing for a given shape. */
export function activatePathEdit(shapeId: string, layerIndex: number, pathData: string): void {
    EventBus.emit('ACTIVATE_PATH_EDIT', { shapeId, layerIndex, pathData });

    // Switch to the path edit tool
    if (typeof (window as any).selectTool === 'function') {
        (window as any).selectTool((window as any).Tool?.PathEdit);
    }
}

/** Deactivates the path editor and reverts to VectorSelect. */
export function deactivatePathEdit(): void {
    _editor?.deactivate();
    if (typeof (window as any).selectTool === 'function') {
        (window as any).selectTool((window as any).Tool?.VectorSelect);
    }
}
